import { gql } from "@apollo/client";
import GraphQlService from "./graphql";

class ProcessGraphql extends GraphQlService {
  async getProcessTemplatesByCategory(category) {
    return this.client
      .query({
        query: gql`
          query getProcessTemplatesByCategory($category: ProcessCategory) {
            getProcessTemplatesByCategory(category: $category) {
              id
              accountId
              nameSw
              nameEng
              journalTypes
              category
              descriptionSw
              descriptionEng
              isPublic
              isActive
              createdAt
              updatedAt
              processTemplateSteps {
                id
                processTemplateId
                nameSw
                nameEng
                taskType
                typeEntityId
                orderIndex
                isRequired
                dueOffsetDays
                dueOffsetReminder
                configJson
              }
            }
          }
        `,
        variables: {
          category: category?.toUpperCase(),
        },
      })
      .then((result) => result?.data?.getProcessTemplatesByCategory);
  }

  async createProcessConfigs(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation createProcessConfigs(
            $createProcessConfigsInput: CreateProcessConfigsInput!
          ) {
            createProcessConfigs(
              createProcessConfigsInput: $createProcessConfigsInput
            ) {
              id
            }
          }
        `,
        variables: {
          createProcessConfigsInput: {
            processTemplateId: data?.processTemplateId,
            reason: data?.reason ?? null,
            journalTypeId: data?.journalTypeId,
            studentIds: data?.studentIds,
            stepEntityOverrides: data?.stepEntityOverrides ?? [],
            attributesJson: data?.attributesJson ?? null,
            isPrivate: data?.isPrivate ?? false,
            sharedCounsellorIds: data?.sharedCounsellorIds ?? [],
          },
        },
      })
      .then((result) => result?.data?.createProcessConfigs);
  }

  async getProcesses(getProcessesInput, language) {
    return this.client
      .query({
        query: gql`
          query GetProcesses(
            $getProcessesInput: GetProcessesInput
            $language: String
          ) {
            getProcesses(
              getProcessesInput: $getProcessesInput
              language: $language
            ) {
              id
              processConfigId
              processTemplateId
              tab
              studentId
              personId
              studentName
              attributeValues
              displayValue
              processStatus
              processCategory
              processCategoryLabel
              processName
              processNameEng
              processNameSw
              startDate
              endDate
              reason
              ownedByMe
              rowState
              nextTaskId
              nextTaskName
              nextTaskNameEng
              nextTaskNameSw
              nextTaskType
              nextTaskStatus
              nextTaskDueDate
              completedAt
              totalTasksCount
              completedTasksCount
              skippedTasksCount
              studentsCount
              accountId
              studentProfilePictureName
              journalTypeId
              journalType {
                id
                nameEng
                nameSw
              }
            }
          }
        `,
        variables: {
          getProcessesInput,
          language,
        },
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.getProcesses);
  }

  async getProcessConfigs(id, userId = null) {
    return this.client
      .query({
        query: gql`
          query GetProcessConfigs($id: String!, $userId: String) {
            getProcessConfigs(id: $id, userId: $userId) {
              id
              accountId
              status
              journalTypeId
              journalType {
                id
                nameEng
                nameSw
              }
              reason
              startDate
              endDate
              processTemplate {
                id
                category
                nameEng
                nameSw
              }
              processConfigsStudents {
                id
                userId
                user {
                  externalId
                  accountPeople {
                    id
                    accountId
                    userId
                    name
                    email
                    gender
                    profilePictureName
                    accountPersonAttributes {
                      id
                      personId
                      name
                      value
                      deleted
                    }
                  }
                }
              }
              isPrivate
              processConfigsAttributes {
                id
                accountId
                attributeValues
              }
              processConfigsPermissions {
                id
                counsellorId
                accountId
                counsellor {
                  accountPeople {
                    id
                    accountId
                    userId
                    name
                    profilePictureName
                  }
                }
              }
              processTasks {
                id
                userId
                status
                dueDate
                completedAt
                entityType
                entityId
                signatureStatus
                signedAt
                signedByName
                referralStatus
                referralStatusAt
                consentStatus
                consentStatusAt
                processTemplateStep {
                  id
                  nameEng
                  nameSw
                  orderIndex
                  taskType
                  typeEntityId
                  isRequired
                }
              }
            }
          }
        `,
        variables: { id, ...(userId && { userId }) },
      })
      .then((result) => result?.data?.getProcessConfigs);
  }

  async updateProcessTask(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation UpdateProcessTask($input: UpdateProcessTaskInput!) {
            updateProcessTask(updateProcessTaskInput: $input) {
              id
              status
              completedAt
              completedBy
              completionMethod
            }
          }
        `,
        variables: {
          input: {
            id: data.id,
            status: data.status,
            completedAt: data.completedAt,
            completedBy: data.completedBy,
            completionMethod: data.completionMethod,
          },
        },
      })
      .then((result) => result?.data?.updateProcessTask);
  }

  async skipProcessTask(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation SkipProcessTask($input: SkipProcessTaskInput!) {
            skipProcessTask(skipProcessTaskInput: $input) {
              id
              status
            }
          }
        `,
        variables: {
          input: {
            id: data.id,
          },
        },
      })
      .then((result) => result?.data?.skipProcessTask);
  }

  async completeInformationTask(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation CompleteInformationTask(
            $input: CompleteInformationTaskInput!
          ) {
            completeInformationTask(completeInformationTaskInput: $input) {
              id
              status
              completedAt
              completedBy
              completionMethod
            }
          }
        `,
        variables: {
          input: {
            id: data.id,
          },
        },
      })
      .then((result) => result?.data?.completeInformationTask);
  }

  async getProcessTaskEvents(processConfigId) {
    return this.client
      .query({
        query: gql`
          query GetProcessTaskEvents($processConfigId: String!) {
            getProcessTaskEvents(processConfigId: $processConfigId) {
              id
              processConfigId
              accountId
              processTaskId
              eventType
              createdAt
              createdByUser {
                id
                email
                accountPeople {
                  name
                  profilePictureName
                  accountId
                }
              }
              payloadJson
              user {
                accountPeople {
                  name
                  profilePictureName
                  accountId
                }
              }
              processTask {
                id
                userId
                status
                entityId
                entityType
                processTemplateStep {
                  nameEng
                  nameSw
                  taskType
                  orderIndex
                }
              }
            }
          }
        `,
        variables: { processConfigId },
        fetchPolicy: "network-only",
      })
      .then((result) => result?.data?.getProcessTaskEvents);
  }

  async getProcessConfigsList() {
    return this.client
      .query({
        query: gql`
          query getProcessConfigsList {
            getProcessConfigsList {
              id
              processTemplateId
              status
              startDate
              endDate
              reason
              processCategory
              processCategoryLabel
              processName
              studentsCount
              attributesCount
            }
          }
        `,
      })
      .then((result) => result?.data?.getProcessConfigsList);
  }
  async createProcessTasksEvents(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation CreateProcessTasksEvents(
            $createProcessTasksEventsInput: CreateProcessTasksEventsInput!
          ) {
            createProcessTasksEvents(
              createProcessTasksEventsInput: $createProcessTasksEventsInput
            ) {
              id
            }
          }
        `,
        variables: {
          createProcessTasksEventsInput: {
            processConfigId: data.processConfigId,
            processTaskId: data.processTaskId ?? null,
            userId: data.userId ?? null,
            accountId: data.accountId,
            eventType: data.eventType,
            payloadJson: data.payloadJson
              ? JSON.stringify(data.payloadJson)
              : null,
          },
        },
      })
      .then((result) => result?.data?.createProcessTasksEvents);
  }

  async getProcessDocumentsByProfileId(
    profileId,
    accountId = null,
    processConfigId,
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getProcessDocumentsByProfileId(
              profileId: "${profileId}",
              accountId: "${accountId}",
              processConfigId: "${processConfigId}"
            ) {
              id
              accountId
              processConfigId
              entityId
              entityType
              resourceName
              resourceType
              contentType
              size
              description
              createdAt
              updatedAt
              createdBy
              updatedBy
              version
              createdByUser {
                id
                accountPeople {
                  id
                  accountId
                  name
                }
              }
            }
          }
        `,
      })
      .then((result) => result?.data?.getProcessDocumentsByProfileId);
  }
}

const processGraphqlInstance = new ProcessGraphql();
export default processGraphqlInstance;
